import React, { useImperativeHandle, useRef, useState, useEffect } from "react";
import classNames from "classnames";
import Button from "@/component/Button";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import { AddIcon, CloseIcon } from "@/assets/svgs/icons";


export interface MultiTextInputGroupRef {
    clear: () => void;
}

interface MultiTextInputGroupProps {
    value: string[];
    label: string;
    placeholder?: string;
    invalid: boolean;
    invalidText: string;
    onChange: (value: string[]) => void;
}

const MultiTextInputGroup = React.forwardRef<MultiTextInputGroupRef, MultiTextInputGroupProps>(( props, ref ) => {
    const { value, label, placeholder, invalid, invalidText, onChange } = props;

    const subKeywordsInputRef = useRef<HTMLInputElement>(null);

    const addSubKeyword = () => {
        if (!subKeywordsInputRef.current) {
            return;
        }
        const newSubKeywords = subKeywordsInputRef.current.value.trim();
        if (!newSubKeywords) {
            return;
        }
        if (value.includes(newSubKeywords)) {
            subKeywordsInputRef.current.value = '';
            return;
        }

        onChange([...value, newSubKeywords]);
        subKeywordsInputRef.current.value = '';
    };

    const onClickDeleteBtn = (index: number) => {
        onChange([...value.filter((_, i) => i !== index)]);
    };

    useImperativeHandle(ref, () => ({
        clear: () => {
            if (subKeywordsInputRef.current) {
                subKeywordsInputRef.current.value = '';
            }
        }
    }));

    return (
        <div className="flex flex-col">
            <div className="input-group-heading">
                {label}
            </div>

            <div className="mt-2 flex gap-2 items-center">
                <input ref={subKeywordsInputRef}
                       type="text"
                       placeholder={placeholder}
                       className={classNames("input-default", invalid && "input-invalid")}
                       onKeyDown={e => (e.key === 'Enter') && addSubKeyword()}
                />

                <button onClick={addSubKeyword} >
                    <AddIcon className="text-black-400 size-4" />
                </button>
            </div>

            {invalid && (
                <span className="mt-1 font-[400] text-[14px] leading-none text-point-red">
                    {invalidText}
                </span>
            )}

            {!!value.length && (
                <ul className="mt-3 flex flex-col gap-1.5">
                    {value.map((v, i) => (
                        <TagItem key={i}
                                 value={v}
                                 onDelete={() => onClickDeleteBtn(i)}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
});


interface TagItemProps {
    value: string;
    onDelete: () => void;
}

const TagItem: React.FC<TagItemProps> = ({ value, onDelete }) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            const element = textRef.current;
            if (!element) {
                return;
            }
            const lineHeight = parseInt(getComputedStyle(element).lineHeight);
            setIsOverflowing(element.scrollHeight > lineHeight * 3);
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => {
            window.removeEventListener('resize', checkOverflow);
        };
    }, [value]);

    return (
        <li className={classNames(
                "py-1.5 px-2 flex flex-col rounded-[30px]",
                "border border-black-300 bg-black-100 font-[400] text-[14px] leading-[1.5] text-black-400"
            )}
        >
            <div className="flex justify-between items-center w-full">
                <div ref={textRef}
                     className={classNames(
                         "max-w-[calc(100%-16px)] break-words pl-1",
                         !isExpanded && "line-clamp-3"
                     )}
                >
                    {value}
                </div>
                <button onClick={onDelete}>
                    <CloseIcon className="size-3.5" />
                </button>
            </div>
            {isOverflowing && (
                <Button size="xs"
                        variant="text-transparent"
                        className="w-full py-0"
                        onClick={() => setIsExpanded(prev => !prev)}
                >
                    <ChevronArrowIcon direction={isExpanded ? "up" : "down"}
                                      className="size-4"
                    />
                </Button>
            )}
        </li>
    );
};


export default MultiTextInputGroup;
